"use client";

import React, { useState, useRef, useEffect } from "react";
import Lenis from "lenis";
import { m as motion, AnimatePresence } from "framer-motion";
import {
  Smartphone,
  Star,
  Download,
  ChevronLeft,
  Github,
  ExternalLink,
} from "lucide-react";
import { App } from "@/lib/data/apps";
import { Section } from "@/components/ui/section";
import { MobileOSView } from "./MobileOSView";

interface DesktopViewProps {
  selectedApp: App | null;
  setSelectedApp: (app: App | null) => void;
  apps?: App[];
}

export const DesktopView = React.memo(
  ({ selectedApp, setSelectedApp, apps }: DesktopViewProps) => {
    const detailsScrollRef = useRef<HTMLDivElement>(null);
    const [longPressApp, setLongPressApp] = useState<App | null>(null);
    const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(
      null
    );

    const handleAppMouseDown = (app: App, e: React.MouseEvent) => {
      e.preventDefault();
      const timer = setTimeout(() => {
        setLongPressApp(app);
      }, 500);
      setLongPressTimer(timer);
    };

    const handleAppMouseUp = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
    };

    const handleAppMouseLeave = () => {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
    };

    const handleAppClick = (app: App, e: React.MouseEvent) => {
      // If we just finished a long press (preview is open), don't open the app
      if (longPressApp?.id === app.id) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      setSelectedApp(app);
    };

    // --- Smooth Scroll (Lenis) Initialization for Details Panel ---
    useEffect(() => {
      if (!detailsScrollRef.current) return;

      const lenis = new Lenis({
        wrapper: detailsScrollRef.current,
        content: detailsScrollRef.current.firstElementChild as HTMLElement,
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: "vertical",
        gestureOrientation: "vertical",
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 2,
        infinite: false,
      });

      function raf(time: number) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }

      requestAnimationFrame(raf);

      return () => {
        lenis.destroy();
      };
    }, []);

    return (
      <>
        <div className="min-h-full relative flex flex-col">
          <Section className="w-full max-w-[1920px] mx-auto relative flex-1 pt-6 pb-4 px-4 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1.6fr] gap-10 lg:gap-16 items-center">
              {/* Phone Section - Mobile OS Interface */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center lg:items-start justify-center h-full"
              >
                <div className="w-full max-w-[360px] sm:max-w-[420px] lg:max-w-[480px] xl:max-w-[500px] mx-auto lg:mx-0 max-h-[calc(100vh-140px)] flex flex-col items-center">
                  <div className="relative w-full h-full max-w-full">
                    <div className="relative aspect-[9/19] h-[75vh] max-h-full min-w-[240px] bg-gradient-to-br from-gray-900 to-black rounded-[3rem] p-2.5 lg:p-3 shadow-2xl border-4 border-gray-800 ring-1 ring-white/10">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-7 bg-black rounded-b-3xl z-20 pointer-events-none" />

                      <div className="relative w-full h-full bg-background rounded-[2.5rem] overflow-hidden [container-type:inline-size]">
                        {/* Mobile OS Interface */}
                        <MobileOSView
                          onAppLaunch={setSelectedApp}
                          currentApp={selectedApp}
                          apps={apps}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Instructions or Indicators */}
                  {!selectedApp && (
                    <div className="mt-6 text-center lg:text-left">
                      <p className="text-xs md:text-sm text-gray-400">
                        Interact with the phone to explore apps
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Right Panel - App Details */}
              <motion.div
                ref={detailsScrollRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="h-full lg:overflow-y-auto p-0 md:p-6 lg:p-8 scrollbar-hide"
              >
                <AnimatePresence mode="wait">
                  {!selectedApp ? (
                    <motion.div
                      key="no-selection"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="h-full flex items-center justify-center"
                    >
                      <div className="text-center space-y-4 max-w-md">
                        <div className="w-20 h-20 rounded-full bg-secondary/50 mx-auto flex items-center justify-center">
                          <Smartphone className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-2xl font-bold">Select an App</h3>
                        <p className="text-muted-foreground">
                          Tap on any app icon in the phone to see details
                        </p>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key={selectedApp.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-6"
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex gap-5 md:gap-8 items-start">
                          <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl md:rounded-[2rem] bg-zinc-800 flex items-center justify-center shadow-lg overflow-hidden flex-shrink-0">
                            {selectedApp.logo ||
                            (selectedApp.screens &&
                              selectedApp.screens.length > 0) ? (
                              <img
                                src={
                                  selectedApp.logo || selectedApp.screens?.[0]
                                }
                                alt={selectedApp.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Smartphone className="w-12 h-12 text-white" />
                            )}
                          </div>
                          <div className="pt-2">
                            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                              {selectedApp.name}
                            </h2>
                            <p className="text-xl md:text-2xl text-muted-foreground/75 mt-3">
                              {selectedApp.tagline}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedApp(null)}
                          className="p-4 rounded-full hover:bg-secondary transition-colors"
                        >
                          <ChevronLeft className="w-8 h-8" />
                        </button>
                      </div>

                      {/* Description */}
                      <div className="space-y-6">
                        <h3 className="text-lg font-mono uppercase tracking-wider text-muted-foreground">
                          About
                        </h3>
                        <p className="text-xl md:text-2xl text-muted-foreground/75 leading-relaxed">
                          {selectedApp.description}
                        </p>
                      </div>

                      {/* Tech Stack */}
                      <div className="space-y-6">
                        <h3 className="text-lg font-mono uppercase tracking-wider text-muted-foreground">
                          Tech Stack
                        </h3>
                        <div className="flex flex-wrap gap-3">
                          {selectedApp.tech?.map((tech, idx) => (
                            <motion.span
                              key={tech}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: idx * 0.05 }}
                              whileHover={{ scale: 1.05, y: -1 }}
                              className="px-5 py-2.5 text-sm font-medium rounded-xl bg-white/10 dark:bg-white/5 text-muted-foreground border border-foreground/20 dark:border-white/10 hover:border-primary/50 transition-colors cursor-default"
                            >
                              {tech}
                            </motion.span>
                          ))}
                        </div>
                      </div>

                      {/* Platforms */}
                      {selectedApp.platform &&
                        selectedApp.platform.length > 0 && (
                          <div className="space-y-6">
                            <h3 className="text-lg font-mono uppercase tracking-wider text-muted-foreground">
                              Available On
                            </h3>
                            <div className="flex flex-wrap gap-3">
                              {selectedApp.platform.map((p, idx) => (
                                <motion.span
                                  key={p}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: idx * 0.05 }}
                                  whileHover={{ scale: 1.05, y: -1 }}
                                  className="px-5 py-2.5 text-sm font-medium rounded-xl bg-white/10 dark:bg-white/5 text-muted-foreground border border-foreground/20 dark:border-white/10 hover:border-primary/50 transition-colors cursor-default"
                                >
                                  {p}
                                </motion.span>
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Links */}
                      {(selectedApp.githubUrl || selectedApp.liveUrl) && (
                        <div className="space-y-6">
                          <h3 className="text-lg font-mono uppercase tracking-wider text-muted-foreground">
                            Links
                          </h3>
                          <div className="flex flex-wrap gap-6">
                            {selectedApp.githubUrl && (
                              <motion.a
                                href={selectedApp.githubUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center gap-3 px-8 py-4 text-lg font-semibold rounded-[1rem] bg-foreground/10 dark:bg-white/10 text-foreground hover:bg-foreground/20 dark:hover:bg-white/20 transition-all border border-foreground/20 dark:border-white/10"
                              >
                                <Github className="w-6 h-6" />
                                View Code
                              </motion.a>
                            )}
                            {selectedApp.liveUrl && (
                              <motion.a
                                href={selectedApp.liveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center gap-3 px-8 py-4 text-lg font-semibold rounded-[1rem] bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md"
                              >
                                <ExternalLink className="w-6 h-6" />
                                View Live
                              </motion.a>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Category & Year */}
                      <div className="pt-4 border-t border-border/50">
                        <div className="flex items-center gap-2 text-muted-foreground/50 font-mono text-[9px] tracking-[0.15em] uppercase">
                          <span className="font-semibold">
                            {selectedApp.category}
                          </span>
                          <span>•</span>
                          <span className="font-semibold">
                            {selectedApp.year}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </Section>
        </div>
      </>
    );
  }
);

DesktopView.displayName = "DesktopView";
